import { useCallback, useRef, useState, useEffect, ReactNode } from 'react';
import { WindowConfig, useWindowStore } from '../stores/windowStore';
import { Icon } from './Icon';

interface FloatingWindowProps {
  config: WindowConfig;
  children: ReactNode;
  onClose?: () => void;
}

/**
 * Draggable, resizable floating window component
 */
export function FloatingWindow({ config, children, onClose }: FloatingWindowProps) {
  const {
    focusWindow,
    moveWindow,
    resizeWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    restoreWindow,
  } = useWindowStore();

  const windowRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeEdge, setResizeEdge] = useState<string | null>(null);

  // Handle drag start
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (config.state === 'maximized') return;

      e.preventDefault();
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - config.position.x,
        y: e.clientY - config.position.y,
      });
      focusWindow(config.id);
    },
    [config.id, config.position, config.state, focusWindow]
  );

  // Handle drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = Math.max(0, e.clientX - dragOffset.x);
      const newY = Math.max(0, e.clientY - dragOffset.y);
      moveWindow(config.id, { x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, config.id, moveWindow]);

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, edge: string) => {
      if (!config.canResize || config.state === 'maximized') return;

      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      setResizeEdge(edge);
      focusWindow(config.id);
    },
    [config.id, config.canResize, config.state, focusWindow]
  );

  // Handle resize
  useEffect(() => {
    if (!isResizing || !resizeEdge) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = windowRef.current?.getBoundingClientRect();
      if (!rect) return;

      let newWidth = config.size.width;
      let newHeight = config.size.height;
      let newX = config.position.x;
      let newY = config.position.y;

      if (resizeEdge.includes('e')) {
        newWidth = e.clientX - config.position.x;
      }
      if (resizeEdge.includes('w')) {
        const deltaX = e.clientX - config.position.x;
        newWidth = config.size.width - deltaX;
        newX = e.clientX;
      }
      if (resizeEdge.includes('s')) {
        newHeight = e.clientY - config.position.y;
      }
      if (resizeEdge.includes('n')) {
        const deltaY = e.clientY - config.position.y;
        newHeight = config.size.height - deltaY;
        newY = e.clientY;
      }

      // Apply min size constraints
      const minWidth = config.minSize?.width || 200;
      const minHeight = config.minSize?.height || 150;

      if (newWidth >= minWidth && newHeight >= minHeight) {
        resizeWindow(config.id, { width: newWidth, height: newHeight });
        if (resizeEdge.includes('w') || resizeEdge.includes('n')) {
          moveWindow(config.id, { x: newX, y: newY });
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeEdge(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeEdge, config, resizeWindow, moveWindow]);

  // Handle close
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
    closeWindow(config.id);
  }, [config.id, closeWindow, onClose]);

  // Handle double-click title to maximize/restore
  const handleTitleDoubleClick = useCallback(() => {
    if (!config.canMaximize) return;

    if (config.state === 'maximized') {
      restoreWindow(config.id);
    } else {
      maximizeWindow(config.id);
    }
  }, [config.id, config.state, config.canMaximize, maximizeWindow, restoreWindow]);

  // Don't render if minimized
  if (config.state === 'minimized' || !config.isVisible) {
    return null;
  }

  const isMaximized = config.state === 'maximized';

  return (
    <div
      ref={windowRef}
      onClick={() => focusWindow(config.id)}
      style={{
        position: 'fixed',
        left: isMaximized ? 0 : config.position.x,
        top: isMaximized ? 0 : config.position.y,
        width: isMaximized ? '100%' : config.size.width,
        height: isMaximized ? '100%' : config.size.height,
        zIndex: config.zIndex,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-surface)',
        border: isMaximized ? 'none' : '1px solid var(--color-border)',
        borderRadius: isMaximized ? 0 : 'var(--radius-md)',
        boxShadow: isMaximized ? 'none' : 'var(--shadow-xl)',
        overflow: 'hidden',
        userSelect: isDragging || isResizing ? 'none' : 'auto',
      }}
    >
      {/* Title bar */}
      <div
        onMouseDown={handleDragStart}
        onDoubleClick={handleTitleDoubleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-xs) var(--spacing-sm)',
          backgroundColor: 'var(--color-surface-elevated)',
          borderBottom: '1px solid var(--color-border)',
          cursor: isMaximized ? 'default' : 'move',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text)',
            fontWeight: 'var(--font-weight-medium)',
            letterSpacing: '0.02em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {config.title}
        </span>

        <div style={{ display: 'flex', gap: '2px' }}>
          {config.canMinimize && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                minimizeWindow(config.id);
              }}
              style={{
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
              }}
              title="Minimize"
            >
              <Icon name="minus" size={12} />
            </button>
          )}
          {config.canMaximize && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isMaximized) {
                  restoreWindow(config.id);
                } else {
                  maximizeWindow(config.id);
                }
              }}
              style={{
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
              }}
              title={isMaximized ? 'Restore' : 'Maximize'}
            >
              <Icon name={isMaximized ? 'minus' : 'plus'} size={12} />
            </button>
          )}
          {config.canClose && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              style={{
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
              }}
              title="Close"
            >
              <Icon name="trash" size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        {children}
      </div>

      {/* Resize handles (only when not maximized) */}
      {config.canResize && !isMaximized && (
        <>
          {/* Edge handles */}
          <div
            onMouseDown={(e) => handleResizeStart(e, 'e')}
            style={{
              position: 'absolute',
              right: 0,
              top: 10,
              bottom: 10,
              width: 4,
              cursor: 'e-resize',
            }}
          />
          <div
            onMouseDown={(e) => handleResizeStart(e, 'w')}
            style={{
              position: 'absolute',
              left: 0,
              top: 10,
              bottom: 10,
              width: 4,
              cursor: 'w-resize',
            }}
          />
          <div
            onMouseDown={(e) => handleResizeStart(e, 's')}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 10,
              right: 10,
              height: 4,
              cursor: 's-resize',
            }}
          />
          <div
            onMouseDown={(e) => handleResizeStart(e, 'n')}
            style={{
              position: 'absolute',
              top: 0,
              left: 10,
              right: 10,
              height: 4,
              cursor: 'n-resize',
            }}
          />
          {/* Corner handles */}
          <div
            onMouseDown={(e) => handleResizeStart(e, 'se')}
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: 10,
              height: 10,
              cursor: 'se-resize',
            }}
          />
          <div
            onMouseDown={(e) => handleResizeStart(e, 'sw')}
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: 10,
              height: 10,
              cursor: 'sw-resize',
            }}
          />
          <div
            onMouseDown={(e) => handleResizeStart(e, 'ne')}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: 10,
              height: 10,
              cursor: 'ne-resize',
            }}
          />
          <div
            onMouseDown={(e) => handleResizeStart(e, 'nw')}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 10,
              height: 10,
              cursor: 'nw-resize',
            }}
          />
        </>
      )}
    </div>
  );
}

/**
 * Minimized window button in taskbar
 */
export function MinimizedWindowButton({ config }: { config: WindowConfig }) {
  const { restoreWindow, focusWindow } = useWindowStore();

  return (
    <button
      onClick={() => {
        restoreWindow(config.id);
        focusWindow(config.id);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-xs)',
        padding: 'var(--spacing-xs) var(--spacing-sm)',
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        color: 'var(--color-text-secondary)',
        fontSize: 'var(--font-size-xs)',
      }}
      title={`Restore ${config.title}`}
    >
      <span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {config.title}
      </span>
    </button>
  );
}

/**
 * Window taskbar for minimized windows
 */
export function WindowTaskbar() {
  const windows = useWindowStore((s) => s.windows);
  const minimizedWindows = Object.values(windows).filter((w) => w.state === 'minimized');

  if (minimizedWindows.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 'var(--spacing-sm)',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 'var(--spacing-xs)',
        padding: 'var(--spacing-xs)',
        backgroundColor: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-md)',
        zIndex: 9999,
      }}
    >
      {minimizedWindows.map((w) => (
        <MinimizedWindowButton key={w.id} config={w} />
      ))}
    </div>
  );
}
